// --- Imports ---
// We import the specific types and constants we created in our contexts and reducers.
// This creates a single, consistent source of truth for our types across the application.
import { IconType } from 'react-icons';
import { SocialState } from '../context/SocialContext';
import { InboxStateCategory } from '../context/InboxContext';
import { FilterActionType } from '../context/Reducers'; 

// Icons
import { GiTicket, GiThreeFriends, GiPartyPopper } from 'react-icons/gi';
import { SiPrometheus } from 'react-icons/si';
import { AiOutlinePicture, AiOutlineGlobal } from 'react-icons/ai';
import { TbUserQuestion, TbCurrentLocation } from "react-icons/tb";
import { MdApproval, MdOutlineDashboardCustomize, MdOutlinePendingActions } from "react-icons/md";


// ========================================================================
// --- Type Definitions (The Core of the Refactor) ---
// ========================================================================
// We create a discriminated union based on the 'searchType' property.

interface BaseHeaderConfig {
    id: number;
    name: string;
    icon: IconType; 
    notificationState?: number;
    headerType?:string
}

interface LinkHeaderConfig extends BaseHeaderConfig {
    searchType: 'link';
    type: `/${string}`; // Enforce that this is a URL path
}

interface CacheHeaderConfig extends BaseHeaderConfig {
    searchType: 'cacheState';
    type: string; 
}

interface PeopleStateHeaderConfig extends BaseHeaderConfig {
    searchType: 'peopleState';
    type: SocialState; 
}

interface InboxStateHeaderConfig extends BaseHeaderConfig {
    searchType: 'inboxState';
    type: InboxStateCategory; 
}

interface ViewStateHeaderConfig extends BaseHeaderConfig {
    searchType: 'state';
    type: string; // The specific view names are local to the EventPage component
}

// --- FIX ---
// The `FilterHeaderConfig` is corrected to work with the discriminated union.
interface FilterHeaderConfig extends BaseHeaderConfig {
    searchType: 'filter';          // A single, literal string to act as the discriminant.
    actionType: FilterActionType;    // A new property to hold the specific filter action.
    type: string;                    // This remains as the payload for the filter.
}

// The master type is a union of all possible specific configurations.
export type HeaderConfig =
    | LinkHeaderConfig
    | PeopleStateHeaderConfig
    | InboxStateHeaderConfig
    | ViewStateHeaderConfig
    | FilterHeaderConfig
    | CacheHeaderConfig


// ========================================================================
// --- The Header Configuration Data ---
// ========================================================================
// We type the main object with our new HeaderConfig union type.
// TypeScript will now enforce that every entry is valid.

export const headerConfigs: Record<string, HeaderConfig[]> = {
    headers: [ ],

    cache : [
        {id: 1, name: 'Events', type: 'events', searchType: 'state', icon: GiPartyPopper, headerType:'cache' },
        {id: 2, name: 'Custom', type: 'custom', searchType: 'state', icon: MdOutlineDashboardCustomize },
        {id: 3, name: 'All', type: 'all', searchType: 'state', icon: AiOutlineGlobal },
    ],

    // This was fixed in the previous step and remains correct.
    inbox : [
        // {id: 1, name: 'Private', type: 'private', searchType: 'inboxState', icon: TbCurrentLocation, headerType:'inbox' },
        {id: 3, name: 'Support', type: 'supportUser', searchType: 'inboxState', icon: TbUserQuestion, headerType:'inbox' },
    ],
    
    // Added for completeness and remains correct.
    people: [
        { id: 1, name: 'Friends', type: 'friends', searchType: 'peopleState', icon: GiThreeFriends, headerType:'people' },
        { id: 2, name: 'Requests', type: 'requests', searchType: 'peopleState', icon: TbUserQuestion },
        { id: 3, name: 'Sent', type: 'requestsSent', searchType: 'peopleState', icon: MdOutlinePendingActions },
        { id: 4, name: 'Discover', type: 'discovery', searchType: 'peopleState', icon: AiOutlineGlobal },
    ],

    event : [
        {id: 1, name: 'Event', type: 'event', searchType: 'state', icon: GiPartyPopper, headerType:'event' },
        {id: 2, name: 'Guests', type: 'guests', searchType: 'state', icon: GiThreeFriends },
        {id: 3, name: 'Approved', type: 'requestApproved', searchType: 'state', icon: MdApproval },
        {id: 4, name: 'Requests', type: 'requests', searchType: 'state', icon: TbUserQuestion },
        {id: 5, name: 'Promoters', type: 'promoters', searchType: 'state', icon: SiPrometheus },
        // {id: 6, name: 'Edit', type: 'edit', searchType: 'state', icon: CiEdit },
        {id: 7, name: 'Packages', type: 'packages', searchType: 'state', icon: GiTicket },
        {id: 8, name: 'Pictures', type: 'pictures', searchType: 'state', icon: AiOutlinePicture },
    ]
};